import React, { useEffect } from 'react';
import { Box, Card, CardContent, Grid, Typography, Chip, Skeleton, LinearProgress } from '@mui/material';
import { SmartToy, CheckCircle, Warning, Wifi, WifiOff, Battery90 } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { normalizeBatteryPercent } from '../../utils/battery';
import { useWebSocket } from '../../hooks/useWebSocket';
import { requestRobotsOnline } from '../../utils/socketClient';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { robots, isLoading } = useAppSelector((state) => state.robots);
  const { user } = useAppSelector((state) => state.auth);
  const { robotsOnline, isConnected, getRobotTelemetry, subscribeToRobot } = useWebSocket();

  useEffect(() => { dispatch(fetchMyRobots()); }, [dispatch]);
  
  // Subscribe once when robots first load (idempotent — socketClient prevents duplicates)
  const subscribedRef = React.useRef(false);
  useEffect(() => {
    if (robots.length > 0 && !subscribedRef.current) {
      robots.forEach((r) => subscribeToRobot(r.serial_number));
      subscribedRef.current = true;
    }
  }, [robots, subscribeToRobot]);
  
  // Request fresh online status after robots load and periodically
  useEffect(() => {
    if (isConnected && robots.length > 0) {
      requestRobotsOnline();
      const interval = setInterval(requestRobotsOnline, 10000);
      return () => clearInterval(interval);
    }
  }, [isConnected, robots.length]);

  // Helper to check if robot is online (from list OR recent telemetry)
  const isRobotOnline = (serial: string) => {
    if (robotsOnline.includes(serial)) return true;
    const telemetry = getRobotTelemetry(serial);
    return telemetry && (Date.now() - telemetry.timestamp < 10000);
  };

  const onlineCount = robots.filter((r) => isRobotOnline(r.serial_number)).length;
  const offlineCount = robots.length - onlineCount;

  const stats = [
    { title: 'Total Robots', value: robots.length, icon: SmartToy, color: '#00d4aa' },
    { title: 'Online', value: onlineCount, icon: CheckCircle, color: '#69f0ae' },
    { title: 'Offline', value: offlineCount, icon: Warning, color: offlineCount > 0 ? '#ffab40' : '#69f0ae' },
    { title: 'Connection', value: isConnected ? 'Active' : 'Lost', icon: isConnected ? Wifi : WifiOff, color: isConnected ? '#40c4ff' : '#ff5252' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Welcome back, {user?.email?.split('@')[0]}</Typography>
        <Typography variant="body1" color="text.secondary">Here's an overview of your robot fleet</Typography>
      </Box>
      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ background: `${stat.color}10`, border: `1px solid ${stat.color}20`, overflow: 'hidden', height: '100%' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    {/* v6.3.34: minWidth:0 lets this column shrink inside the
                        flex row — without it a long value pushed the card past
                        its grid column. All four tiles share one value size so
                        they render identically; height:100% on the Card keeps
                        the row even. */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom noWrap>{stat.title}</Typography>
                      <Typography
                        fontWeight={700}
                        sx={{
                          color: stat.color,
                          lineHeight: 1.2,
                          fontSize: typeof stat.value === 'number'
                            ? { xs: '2rem', sm: '1.75rem', md: '2rem', lg: '2.5rem' }
                            : { xs: '1.5rem', sm: '1.25rem', md: '1.4rem', lg: '1.75rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {/* Render the icon directly so sx applies — cloneElement was
                          dropping color in newer MUI builds, leaving an empty
                          colored tile (the Connection card looked icon-less). */}
                      <Icon sx={{ color: stat.color, fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Fleet Overview</Typography>
      {isLoading ? (
        <Grid container spacing={2}>{[1, 2, 3].map((i) => <Grid item xs={12} md={6} lg={4} key={i}><Skeleton variant="rounded" height={200} /></Grid>)}</Grid>
      ) : robots.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}><SmartToy sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} /><Typography variant="h6" color="text.secondary">No robots assigned yet</Typography></Card>
      ) : (
        <Grid container spacing={2}>
          {robots.map((robot) => {
            const isOnline = isRobotOnline(robot.serial_number);
            const telemetry = getRobotTelemetry(robot.serial_number);
            return (
              <Grid item xs={12} md={6} lg={4} key={robot.id}>
                <Card sx={{ height: '100%', border: isOnline ? '1px solid rgba(105, 240, 174, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                        {/* v6.3.36: serial number is the primary identifier —
                            friendly names are user-set and can collide, so a
                            bold duplicate name made two robots indistinguishable. */}
                        <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-all', lineHeight: 1.3 }}>
                          {robot.serial_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {robot.friendly_name || '—'}
                          {robot.model_number ? ` · ${robot.model_number}` : ''}
                        </Typography>
                      </Box>
                      <Chip size="small" label={isOnline ? 'Online' : 'Offline'} sx={{ flexShrink: 0, backgroundColor: isOnline ? 'rgba(105, 240, 174, 0.2)' : 'rgba(255, 82, 82, 0.2)', color: isOnline ? '#69f0ae' : '#ff5252' }} />
                    </Box>
                    {isOnline && telemetry?.battery?.percentage != null && (() => {
                      // Normalize once — ROS publishes 0–1, other stacks 0–100.
                      const pct = normalizeBatteryPercent(telemetry.battery.percentage) ?? 0;
                      const barColor = pct > 50 ? '#69f0ae' : pct > 20 ? '#ffab40' : '#ff5252';
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Battery90 sx={{ fontSize: 18, color: 'text.secondary' }} /><Typography variant="body2">Battery</Typography></Box>
                            <Typography variant="body2" fontWeight={600}>{pct.toFixed(0)}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: barColor } }} />
                        </Box>
                      );
                    })()}
                    {isOnline && telemetry?.pose?.x != null && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="caption" color="text.secondary">Position X</Typography><Typography variant="body2" fontWeight={600}>{(telemetry.pose.x ?? 0).toFixed(2)} m</Typography></Box>
                        <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="caption" color="text.secondary">Position Y</Typography><Typography variant="body2" fontWeight={600}>{(telemetry.pose.y ?? 0).toFixed(2)} m</Typography></Box>
                      </Box>
                    )}
                    {!isOnline && <Box sx={{ p: 2, textAlign: 'center', borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="body2" color="text.secondary">Robot is offline</Typography></Box>}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
